import { isValid, parse } from 'date-fns';
import { logger } from 'src/analytics/KatalLogger';
import { BUSINESS_GROUP_CODES, DMM_FIELDS_MAPPING } from 'src/constants/business-group-constants';
import { BusinessSegmentNames, CorpSegmentNames } from 'src/constants/corp-segment-constants';
import { BusinessGroupEntity, BusinessSegmentDataType, MasterBusinessSegments } from 'src/models/AppContextModels';
import { CountryCompanyMapping, DMMProductLineCampaignFlatten } from 'src/models/DmmModel';
import { ForecastGridRowData, ForecastTemplateColumns, ForecastTemplateMasterCorpSegmentDropdownValues } from 'src/models/ForecastModels';
import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { getCountryCodeFromCompany } from '../forecast-utils/column-generator/helpers/DMMColumnUtils';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ValidationService } from './ValidationService';

/**
 * Service for validating segments in forecast templates
 */
export class SegmentValidationService extends ValidationService {
  /**
   * Validates the corp segment and business segment fields in the modified rows.
   * @param forecastTemplateRelevantRowData The array of modified rows to validate.
   * @param forecastTemplateColumns The column definitions including segment fields.
   * @param corpSegmentDropdownValues The array of master corp segment dropdown values.
   * @param masterBusinessSegments The array of master business segments.
   * @param expenseTypesForCurrentGroup The array of expense types for the current group.
   * @param businessGroupShortDesc The short description of the business group.
   * @param businessGroup The business group entity.
   * @returns Promise with validation status
   */
  public async validateSegments(
    forecastTemplateRelevantRowData: ForecastGridRowData[],
    forecastTemplateColumns: ForecastTemplateColumns,
    corpSegmentDropdownValues: ForecastTemplateMasterCorpSegmentDropdownValues[],
    masterBusinessSegments: MasterBusinessSegments[],
    expenseTypesForCurrentGroup: string[],
    businessGroupShortDesc: string,
    businessGroup: BusinessGroupEntity
  ): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];

    logger.info(`Validating segments for ${forecastTemplateRelevantRowData.length} records`);

    const { corpSegmentMandatoryFields, businessSegmentColumns } = forecastTemplateColumns;
    const corpSegmentFieldsWithoutDescription = corpSegmentMandatoryFields.filter((field) => !field.endsWith(' Description'));

    // Only validate rows that are new or have edited segments
    const rowsToValidate = forecastTemplateRelevantRowData.filter(
      (rowData) => rowData[ForecastGridFixedFields.IsNewFERow.value] || rowData[ForecastGridFixedFields.IsSegmentEdited.value]
    );

    rowsToValidate.forEach((row) => {
      this.validateCorpSegmentFields(row, corpSegmentFieldsWithoutDescription, corpSegmentDropdownValues, validationErrorDetails);
      this.validateBusinessSegmentFields(
        row,
        businessSegmentColumns,
        businessGroup,
        masterBusinessSegments,
        expenseTypesForCurrentGroup,
        businessGroupShortDesc,
        validationErrorDetails
      );
    });

    // Validate edited rows that are not new rows
    const editedRowsToValidate = forecastTemplateRelevantRowData.filter(
      (rowData) => !rowData[ForecastGridFixedFields.IsNewFERow.value] && rowData[ForecastGridFixedFields.IsEdited.value]
    );

    if (editedRowsToValidate.length > 0) {
      logger.info(`Validating date and number fields for ${editedRowsToValidate.length} edited rows`);

      // For edited rows, we only need to validate date and number business segments
      this.validateDateAndNumberFieldsForEditedRows(editedRowsToValidate, businessSegmentColumns, businessGroup, validationErrorDetails);
    }

    // Log validation errors if any
    this.logValidationErrors('Segments', validationErrorDetails);

    // Return appropriate validation status
    return this.createValidationStatus(
      validationErrorDetails,
      ForecastValidationMessages.SEGMENTS_VALIDATION_IN_SUCCESS,
      ForecastValidationMessages.SEGMENTS_VALIDATION_IN_FAILED,
      ForecastValidationMessages.SEGMENTS_VALIDATION_DEFAULT
    );
  }

  /**
   * Validates date and number fields for edited rows
   * @param editedRows The edited rows to validate
   * @param businessSegmentColumns The business segment columns
   * @param businessGroup The business group entity
   * @param validationErrorDetails Array to collect validation errors
   */
  private validateDateAndNumberFieldsForEditedRows(
    editedRows: ForecastGridRowData[],
    businessSegmentColumns: string[],
    businessGroup: BusinessGroupEntity,
    validationErrorDetails: ValidationErrorDetail[]
  ): void {
    editedRows.forEach((row) => {
      businessSegmentColumns.forEach((businessSegmentField) => {
        const segmentDataType = this.getBusinessSegmentDataType(businessGroup, businessSegmentField);

        // For edited rows, we only validate date and number fields
        if (segmentDataType === 'date') {
          this.validateDateBusinessSegment(row, businessSegmentField, validationErrorDetails);
        } else if (segmentDataType === 'date_string') {
          this.validateDateAsStringBusinessSegment(row, businessSegmentField, validationErrorDetails);
        } else if (segmentDataType === 'number') {
          this.validateNumberBusinessSegment(row, businessSegmentField, validationErrorDetails);
        }
      });
    });
  }

  /**
   * Validates corp segment fields for a row
   * @param row The row data to validate
   * @param corpSegmentFieldsWithoutDescription The corp segment fields without description
   * @param corpSegmentDropdownValues The dropdown values for corp segments
   * @param validationErrorDetails Array to collect validation errors
   */
  private validateCorpSegmentFields(
    row: ForecastGridRowData,
    corpSegmentFieldsWithoutDescription: string[],
    corpSegmentDropdownValues: ForecastTemplateMasterCorpSegmentDropdownValues[],
    validationErrorDetails: ValidationErrorDetail[]
  ): void {
    corpSegmentFieldsWithoutDescription.forEach((field) => {
      const dropdownValue = corpSegmentDropdownValues.find((dropdown) => dropdown.masterCorpSegmentDisplayName === field);

      if (!dropdownValue) {
        validationErrorDetails.push({
          message: `Dropdown values for Corp Segment field: ${field} are missing.`
        });
        return;
      }

      const isValid = dropdownValue.masterCorpSegmentDropdownValues.some((option) => option.label === row[field]);

      if (!isValid) {
        validationErrorDetails.push({
          message: `Invalid value ${row[field] ? `'${row[field]}'` : ' '} for ${field} - ${this.getLineItemIdMessagePart(
            row,
            ForecastGridFixedFields.XptLineItemId.value
          )}`
        });
      }
    });
  }

  /**
   * Validates business segment fields for a row
   * @param row The row data to validate
   * @param businessSegmentColumns The business segment columns
   * @param businessGroup The business group entity
   * @param masterBusinessSegments The master business segments
   * @param expenseTypesForCurrentGroup The expense types for the current group
   * @param businessGroupShortDesc The business group short description
   * @param validationErrorDetails Array to collect validation errors
   */
  private validateBusinessSegmentFields(
    row: ForecastGridRowData,
    businessSegmentColumns: string[],
    businessGroup: BusinessGroupEntity,
    masterBusinessSegments: MasterBusinessSegments[],
    expenseTypesForCurrentGroup: string[],
    businessGroupShortDesc: string,
    validationErrorDetails: ValidationErrorDetail[]
  ): void {
    businessSegmentColumns.forEach((businessSegmentField) => {
      const segmentDataType = this.getBusinessSegmentDataType(businessGroup, businessSegmentField);

      switch (segmentDataType) {
        case 'dropdown':
          this.validateDropdownBusinessSegment(
            row,
            businessSegmentField,
            masterBusinessSegments,
            expenseTypesForCurrentGroup,
            businessGroupShortDesc,
            validationErrorDetails
          );
          break;
        case 'date':
          this.validateDateBusinessSegment(row, businessSegmentField, validationErrorDetails);
          break;
        case 'number':
          this.validateNumberBusinessSegment(row, businessSegmentField, validationErrorDetails);
          break;
        default:
          // No validation needed for text fields
          break;
      }
    });
  }

  /**
   * Gets the data type for a business segment
   * @param businessGroup The business group entity
   * @param businessSegmentField The business segment field name
   * @returns The business segment data type
   */
  private getBusinessSegmentDataType(businessGroup: BusinessGroupEntity, businessSegmentField: string): BusinessSegmentDataType {
    return (
      businessGroup.business_segments.find((segment) => segment.business_segment_name === businessSegmentField)?.business_segment_data_type || 'text'
    );
  }

  /**
   * Validates a dropdown business segment field
   * @param row The row data to validate
   * @param businessSegmentField The business segment field name
   * @param masterBusinessSegments The master business segments
   * @param expenseTypesForCurrentGroup The expense types for the current group
   * @param businessGroupShortDesc The business group short description
   * @param validationErrorDetails Array to collect validation errors
   */
  private validateDropdownBusinessSegment(
    row: ForecastGridRowData,
    businessSegmentField: string,
    masterBusinessSegments: MasterBusinessSegments[],
    expenseTypesForCurrentGroup: string[],
    businessGroupShortDesc: string,
    validationErrorDetails: ValidationErrorDetail[]
  ): void {
    const masterSegment = masterBusinessSegments.find((segment) => segment.segment_name === businessSegmentField);

    if (!masterSegment) {
      validationErrorDetails.push({
        message: `Dropdown values for Business Segment field: ${businessSegmentField} are missing.`
      });
      return;
    }

    // Skip DMM-specific validation here as it's handled separately
    if (businessGroupShortDesc !== BUSINESS_GROUP_CODES.DMM) {
      const isValid = this.isValidDropdownValue(row, businessSegmentField, masterSegment, expenseTypesForCurrentGroup);

      if (!isValid) {
        validationErrorDetails.push({
          message: `Invalid value ${
            row[businessSegmentField] ? `'${row[businessSegmentField]}'` : ' '
          } for ${businessSegmentField} - ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
        });
      }
    }
  }

  /**
   * Checks if a dropdown value is valid
   * @param row The row data to validate
   * @param businessSegmentField The business segment field name
   * @param masterSegment The master segment
   * @param expenseTypesForCurrentGroup The expense types for the current group
   * @returns True if the value is valid, false otherwise
   */
  private isValidDropdownValue(
    row: ForecastGridRowData,
    businessSegmentField: string,
    masterSegment: MasterBusinessSegments,
    expenseTypesForCurrentGroup: string[]
  ): boolean {
    if (businessSegmentField === BusinessSegmentNames.EXPENSE_TYPE) {
      return expenseTypesForCurrentGroup.some((expenseType) => expenseType === row[businessSegmentField]);
    } else {
      return masterSegment.business_segment_dropdown_list.some((option) => option === row[businessSegmentField]);
    }
  }

  /**
   * Validates a date business segment field
   * @param row The row data to validate
   * @param businessSegmentField The business segment field name
   * @param validationErrorDetails Array to collect validation errors
   */
  private validateDateBusinessSegment(row: ForecastGridRowData, businessSegmentField: string, validationErrorDetails: ValidationErrorDetail[]): void {
    const dateValue = row[businessSegmentField];

    // Skip validation if the field is empty
    if (!dateValue) {
      return;
    }

    // Check if the value is a number (Excel serial date)
    if (typeof dateValue === 'number' || (!isNaN(Number(dateValue)) && Number.isInteger(Number(dateValue)))) {
      validationErrorDetails.push({
        message: `Invalid date format '${dateValue}' for ${businessSegmentField} - ${this.getLineItemIdMessagePart(
          row,
          ForecastGridFixedFields.XptLineItemId.value
        )}. Expected format: YYYY-MM-DD.`
      });
      return;
    }

    // Check if the date is in YYYY-MM-DD format
    const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(dateValue);
    if (!isValidFormat) {
      validationErrorDetails.push({
        message: `Invalid date format '${dateValue}' for ${businessSegmentField} - ${this.getLineItemIdMessagePart(
          row,
          ForecastGridFixedFields.XptLineItemId.value
        )}. Expected format: YYYY-MM-DD.`
      });
      return;
    }

    const parsedDate = parse(dateValue, 'yyyy-MM-dd', new Date());

    // Check if the date is valid using date-fns isValid
    if (!isValid(parsedDate)) {
      validationErrorDetails.push({
        message: `Invalid date value '${dateValue}' for ${businessSegmentField} - ${this.getLineItemIdMessagePart(
          row,
          ForecastGridFixedFields.XptLineItemId.value
        )}. The date is not valid.`
      });
    }
  }

  /**
   * Validates a date business segment field in YYYYMM format
   * @param row The row data to validate
   * @param businessSegmentField The business segment field name
   * @param validationErrorDetails Array to collect validation errors
   * @returns The updated validation error details array
   */
  private validateDateAsStringBusinessSegment(
    row: ForecastGridRowData,
    businessSegmentField: string,
    validationErrorDetails: ValidationErrorDetail[]
  ): ValidationErrorDetail[] {
    const dateValue = row[businessSegmentField];

    // Skip validation if the field is empty
    if (!dateValue) {
      return validationErrorDetails;
    }

    // Convert to string if it's a number
    let dateValueStr = String(dateValue);

    // Check if the date is in YYYYMM format
    const isValidFormat = /^\d{6}$/.test(dateValueStr);
    if (!isValidFormat) {
      validationErrorDetails.push({
        message: `Invalid date format '${dateValue}' for ${businessSegmentField} - ${this.getLineItemIdMessagePart(
          row,
          ForecastGridFixedFields.XptLineItemId.value
        )}. Expected format: YYYYMM.`
      });
      return validationErrorDetails;
    }

    // Extract year and month from the YYYYMM format
    const year = dateValueStr.substring(0, 4);
    const month = dateValueStr.substring(4, 6);

    // Construct a date in ISO format for validation (using the first day of the month)
    const dateForValidation = `${year}-${month}-01`;
    const parsedDate = parse(dateForValidation, 'yyyy-MM-dd', new Date());

    // Check if the date is valid using date-fns isValid
    if (!isValid(parsedDate)) {
      validationErrorDetails.push({
        message: `Invalid date value '${dateValue}' for ${businessSegmentField} - ${this.getLineItemIdMessagePart(
          row,
          ForecastGridFixedFields.XptLineItemId.value
        )}. The date is not valid.`
      });
    }

    return validationErrorDetails;
  }

  /**
   * Validates a number business segment field
   * @param row The row data to validate
   * @param businessSegmentField The business segment field name
   * @param validationErrorDetails Array to collect validation errors
   */
  private validateNumberBusinessSegment(
    row: ForecastGridRowData,
    businessSegmentField: string,
    validationErrorDetails: ValidationErrorDetail[]
  ): void {
    const value = row[businessSegmentField];

    // Skip validation if the field is empty
    if (!value) {
      return;
    }

    // Check if value is numeric
    if (typeof value !== 'number' || Number.isNaN(value)) {
      validationErrorDetails.push({
        message: `Invalid number value '${value}' for ${businessSegmentField} - ${this.getLineItemIdMessagePart(
          row,
          ForecastGridFixedFields.XptLineItemId.value
        )}. Expected a valid numeric value.`
      });
    }
  }

  /**
   * Creates a validation status based on validation errors
   * @param validationErrorDetails The validation error details
   * @param successMessage The success message
   * @param errorMessage The error message
   * @param defaultMessage The default message
   * @returns The validation status
   */
  private createValidationStatus(
    validationErrorDetails: ValidationErrorDetail[],
    successMessage: string,
    errorMessage: string,
    defaultMessage: string
  ): ValidationStatusEntity {
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(errorMessage, defaultMessage, validationErrorDetails);
    }
    return this.createSuccessStatus(successMessage, defaultMessage);
  }

  /**
   * Validates business segment combinations against campaign data for DMM
   * @param forecastTemplateRelevantRowData The data to validate
   * @param campaignsData The master campaign data to validate against
   * @param countryCompanyMapping The country-company mapping data
   * @returns Promise with validation status
   */
  public async validateDMMBusinessSegments(
    forecastTemplateRelevantRowData: any[],
    campaignsData: DMMProductLineCampaignFlatten[],
    countryCompanyMapping: CountryCompanyMapping[]
  ): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];

    logger.info(`Validating DMM business segments for ${forecastTemplateRelevantRowData.length} records`);

    forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
      const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;

      if (!this.validateProductLineExists(row, rowNo, validationErrorDetails)) {
        return;
      }

      this.validateDMMCampaignMatch(row, rowNo, campaignsData, countryCompanyMapping, validationErrorDetails);
    });

    // Log validation errors if any
    this.logValidationErrors('DMM Business Segments', validationErrorDetails);

    // Return appropriate validation status
    return this.createValidationStatus(
      validationErrorDetails,
      ForecastValidationMessages.DMM_SEGMENTS_VALIDATION_SUCCESS,
      ForecastValidationMessages.DMM_SEGMENTS_VALIDATION_FAILED,
      ForecastValidationMessages.DMM_SEGMENTS_VALIDATION_DEFAULT
    );
  }

  /**
   * Validates that product line exists in the row
   * @param row The row data to validate
   * @param rowNo The row number
   * @param validationErrorDetails Array to collect validation errors
   * @returns True if product line exists, false otherwise
   */
  private validateProductLineExists(row: any, rowNo: number, validationErrorDetails: ValidationErrorDetail[]): boolean {
    if (!row[CorpSegmentNames.PRODUCT_LINE]) {
      validationErrorDetails.push({
        rowIndex: rowNo,
        message: this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)
          ? `Product Line is required ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
          : 'Product Line is required'
      });
      return false;
    }
    return true;
  }

  /**
   * Validates that the DMM campaign matches
   * @param row The row data to validate
   * @param rowNo The row number
   * @param campaignsData The campaigns data
   * @param countryCompanyMapping The country-company mapping
   * @param validationErrorDetails Array to collect validation errors
   */
  private validateDMMCampaignMatch(
    row: any,
    rowNo: number,
    campaignsData: DMMProductLineCampaignFlatten[],
    countryCompanyMapping: CountryCompanyMapping[],
    validationErrorDetails: ValidationErrorDetail[]
  ): void {
    const productLine = row[CorpSegmentNames.PRODUCT_LINE];
    const company = row[CorpSegmentNames.COMPANY];
    const countryCode = getCountryCodeFromCompany(company, countryCompanyMapping);

    // Filter campaigns by product line and country first
    const relevantCampaigns = campaignsData.filter(
      (campaign) => campaign.product_code === productLine && campaign.country_code === countryCode && campaign.is_active
    );

    const fieldMapping = this.createDMMFieldMapping(row);
    const matchingCampaign = this.findMatchingCampaign(fieldMapping, relevantCampaigns);

    if (!matchingCampaign) {
      validationErrorDetails.push({
        rowIndex: rowNo,
        message: this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)
          ? `Invalid business segment combination for Line item id ${this.getLineItemIdMessagePart(
              row,
              ForecastGridFixedFields.XptLineItemId.value
            )} at row no: ${rowNo}`
          : `Invalid business segment combination at row no: ${rowNo}`
      });
    }
  }

  /**
   * Creates a field mapping for DMM validation
   * @param row The row data
   * @returns The field mapping
   */
  private createDMMFieldMapping(row: any): Record<string, string> {
    const fieldMapping: Record<string, string> = {};

    for (const [businessField, campaignField] of Object.entries(DMM_FIELDS_MAPPING)) {
      if (row[businessField] !== null && row[businessField] !== undefined) {
        fieldMapping[campaignField] = row[businessField];
      }
    }

    return fieldMapping;
  }

  /**
   * Finds a matching campaign
   * @param fieldMapping The field mapping
   * @param relevantCampaigns The relevant campaigns
   * @returns The matching campaign or undefined
   */
  private findMatchingCampaign(
    fieldMapping: Record<string, string>,
    relevantCampaigns: DMMProductLineCampaignFlatten[]
  ): DMMProductLineCampaignFlatten | undefined {
    return relevantCampaigns.find((campaign) => {
      return Object.entries(fieldMapping).every(([campaignField, value]) => {
        const campaignValue = campaign[campaignField as keyof DMMProductLineCampaignFlatten];
        // Treat null, undefined, and empty string as equivalent empty values
        const normalizedCampaignValue = campaignValue ?? '';
        const normalizedValue = value ?? '';
        return normalizedCampaignValue === normalizedValue;
      });
    });
  }
}

// Export singleton instance
export const segmentValidationService = new SegmentValidationService();
